import React, { useEffect, useState } from 'react';
import { useSpring, animated } from 'react-spring';
import { useDrag } from '@use-gesture/react';
import { useQueryClient } from '@tanstack/react-query';
import { useFeedProfiles, useSwipeProfile, useNotificationCounters, queryKeys } from '@/hooks/api';
import { useFeed, useUI, useAppStore, useFilters } from '@/store';
import { Button, LoadingScreen, Card, Badge, Avatar } from '@/components/ui';
import { TelegramUtils } from '@/utils/telegram';
import { api } from '@/api';
import { formatDistance, getBadgeLevelColor, getScoreColor, capitalizeBadgeLevel } from '@/utils';
import { mapCardToProfileSummary } from '@/utils/mappers';
import { cn } from '@/utils';
import type { ProfileSummary, SwipeDirection } from '@/types';
import logoLight from '@/assets/itinder.svg';
import logoDark from '@/assets/itinder_dark.svg';
import matchIconLight from '@/assets/match.svg';
import matchIconDark from '@/assets/match_dark.svg';
import profileIconLight from '@/assets/profile.svg';
import profileIconDark from '@/assets/profile_dark.svg';
import filterIcon from '@/assets/filter.svg';
import filterIconWhite from '@/assets/filter_white.svg';
import ProfileDetailScreen from './ProfileDetailScreen';

const FeedScreen: React.FC = () => {
  const { profiles, currentIndex, loading, hasMore } = useFeed();
  const { setActiveScreen, activeScreen } = useUI();
  const { data, isLoading, error, refetch } = useFeedProfiles();
  const swipeProfile = useSwipeProfile();
  const { ui, setViewingProfile, addFeedProfiles, setFeedLoading, setFeedHasMore, toggleFeedType } = useAppStore();
  const { data: notificationCounters } = useNotificationCounters();
  const { hasActiveFilters } = useFilters();
  const [isResetting, setIsResetting] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const queryClient = useQueryClient();

  // Configure Telegram UI
  useEffect(() => {
    TelegramUtils.configureBackButton(false);
    TelegramUtils.configureMainButton({ visible: false });
  }, []);

  // Invalidate feed when returning to screen (e.g., after swiping in Matches)
  useEffect(() => {
    if (activeScreen === 'feed') {
      // Invalidate to mark data as stale, will refetch on next render if needed
      queryClient.invalidateQueries(queryKeys.feedProfiles);
    }
  }, [activeScreen, queryClient]);

  // Auto-load more profiles when running low
  useEffect(() => {
    const remainingProfiles = profiles.length - currentIndex;
    
    // Preload when 2 or fewer profiles remain (including 0) and more are available
    if (remainingProfiles <= 2 && hasMore && !loading && !isLoading && !isLoadingMore) {
      console.log('🔄 Preloading more profiles (remaining:', remainingProfiles, ')');
      
      const loadMoreProfiles = async () => {
        setIsLoadingMore(true);
        try {
          console.log('🔄 Fetching more cards from API...');
          const cards = await api.getDiscoveryCards(10);
          console.log('📦 Received cards from API:', cards);
          
          if (cards && cards.length > 0) {
            console.log('🖼️ First card photos:', cards[0]?.photos);
            
            const newProfiles = cards.map(card => {
              const profile = mapCardToProfileSummary(card);
              console.log(`📸 Mapped profile ${profile.name}:`, {
                id: profile.id,
                name: profile.name,
                photosCount: profile.photos?.length,
                photos: profile.photos,
              });
              return profile;
            });
            
            addFeedProfiles(newProfiles);
            console.log('✅ Added', newProfiles.length, 'more profiles to feed');
            
            // If we got less than requested, no more profiles available
            if (cards.length < 10) {
              setFeedHasMore(false);
              console.log('🏁 No more profiles available (got less than 10)');
            }
          } else {
            // No profiles returned - mark as no more available
            setFeedHasMore(false);
            console.log('🏁 No more profiles available from server');
          }
        } catch (error) {
          console.error('❌ Failed to load more profiles:', error);
          // On error, stop trying to load more to prevent infinite loop
          setFeedHasMore(false);
        } finally {
          setIsLoadingMore(false);
        }
      };
      
      loadMoreProfiles();
    }
  }, [currentIndex, profiles.length, loading, isLoading, isLoadingMore, hasMore, addFeedProfiles, setFeedHasMore]);

  // Debug
  useEffect(() => {
    console.log('📊 Feed Screen State:', {
      profilesCount: profiles.length,
      currentIndex,
      loading,
      isLoading,
      dataItems: data?.length,
      hasError: !!error,
    });
  }, [profiles, currentIndex, loading, isLoading, data, error]);

  // Preload photos for current and next profile for smooth navigation
  useEffect(() => {
    if (profiles.length === 0) return;
    
    const currentProfile = profiles[currentIndex];
    const nextProfile = profiles[currentIndex + 1];
    
    if (currentProfile?.photos) {
      currentProfile.photos.forEach(photoUrl => {
        const img = new Image();
        img.src = photoUrl;
      });
    }
    if (nextProfile?.photos) {
      nextProfile.photos.forEach(photoUrl => {
        const img = new Image();
        img.src = photoUrl;
      });
    }
  }, [profiles, currentIndex]);

  // Select correct icons based on theme
  const logo = TelegramUtils.isLightTheme ? logoDark : logoLight;
  const matchIcon = TelegramUtils.isLightTheme ? matchIconDark : matchIconLight;
  const profileIcon = TelegramUtils.isLightTheme ? profileIconDark : profileIconLight;
  const filterIconSrc = TelegramUtils.isLightTheme ? filterIconWhite : filterIcon;

  if (isLoading || loading) {
    return <LoadingScreen message="Ищем интересных людей..." />;
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-6">
        <p className="text-tg-text mb-4">Не удалось загрузить профили</p>
        <Button onClick={() => window.location.reload()}>
          Попробовать снова
        </Button>
      </div>
    );
  }

  const handleResetPassSwipes = async () => {
    const confirmed = await TelegramUtils.showConfirm(
      'Вы уверены, что хотите посмотреть пропущенные профили заново?'
    );

    if (!confirmed) return;

    setIsResetting(true);
    try {
      const result = await (api as any).resetPassSwipes();
      
      console.log('✅ Reset pass swipes result:', result);
      TelegramUtils.haptic.success();
      
      // Reset hasMore flag to allow loading new profiles
      setFeedHasMore(true);
      
      // Refresh feed to load profiles again
      await refetch();
      
      await TelegramUtils.showAlert('Профили обновлены! Теперь вы можете посмотреть их заново.');
    } catch (error: any) {
      console.error('❌ Failed to reset pass swipes:', error);
      TelegramUtils.haptic.error();
      await TelegramUtils.showAlert('Не удалось обновить профили. Попробуйте позже.');
    } finally {
      setIsResetting(false);
    }
  };

  // Show loading screen while loading more profiles (to prevent empty state flicker)
  if (!profiles.length && isLoadingMore) {
    return <LoadingScreen message="Загружаем новые профили..." />;
  }

  // Show empty state only if no profiles AND no more available
  if (!profiles.length && !hasMore) {
    return (
      <div className="min-h-screen bg-tg-bg safe-area-y">
        {/* Header - Keep navigation */}
        <div className="flex items-center justify-between py-3 border-b border-white/5 px-6">
          {/* Logo - Clickable to toggle feed type */}
          <button
            onClick={() => {
              toggleFeedType();
              TelegramUtils.haptic.selection();
            }}
            className="hover:opacity-80 transition-opacity"
          >
            <img 
              src={logo} 
              alt="ITinder" 
              className="h-7 w-auto"
            />
          </button>
          
          {/* Icons */}
          <div className="flex items-center gap-2">
            {/* Filter icon */}
            <button
              onClick={() => {
                setActiveScreen('filters');
                TelegramUtils.haptic.selection();
              }}
              className="relative h-7 w-7 flex items-center justify-center hover:opacity-80 transition-opacity"
            >
              <img 
                src={filterIconSrc} 
                alt="Filters" 
                className="h-6 w-6"
              />
              {hasActiveFilters && (
                <div className="absolute top-0 right-0 w-2 h-2 bg-primary-500 rounded-full border border-tg-bg" />
              )}
            </button>
            
            {/* Messages icon */}
            <button
              onClick={() => setActiveScreen('matches')}
              className="relative h-7 w-7 flex items-center justify-center hover:opacity-80 transition-opacity"
            >
              <img 
                src={matchIcon} 
                alt="Matches" 
                className="h-7 w-7"
              />
              {notificationCounters && notificationCounters.unread_messages_count > 0 && (
                <div className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-danger-500 rounded-full flex items-center justify-center border border-tg-bg">
                  <span className="text-white text-xs font-bold leading-none">{notificationCounters.unread_messages_count}</span>
                </div>
              )}
            </button>
            
            {/* Profile icon */}
            <button
              onClick={() => setActiveScreen('profile')}
              className="h-7 w-7 flex items-center justify-center hover:opacity-80 transition-opacity"
            >
              <img 
                src={profileIcon} 
                alt="Profile" 
                className="h-6 w-6"
              />
            </button>
          </div>
        </div>

        {/* Empty State */}
        <div className="flex flex-col items-center justify-center px-6 py-16 text-center" style={{ minHeight: 'calc(100vh - 60px)' }}>
          <div className="w-full max-w-sm">
            {/* Icon */}
            <div className="relative mb-8">
              <div className="w-32 h-32 mx-auto bg-white/5 rounded-full flex items-center justify-center border border-white/10">
                <svg className="w-16 h-16 text-white/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              {/* Subtle rotating ring */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-36 h-36 border border-white/5 rounded-full animate-spin" style={{ animationDuration: '8s' }} />
              </div>
            </div>

            {/* Text */}
            <h2 className="text-2xl font-bold text-white mb-3">
              Профили закончились
            </h2>
            <p className="text-neutral-400 mb-8">
              Загляните позже — новые профили появятся совсем скоро
            </p>

            {/* Actions */}
            <div className="space-y-3">
              <button
                onClick={handleResetPassSwipes}
                disabled={isResetting}
                className="w-full h-14 bg-white text-black rounded-2xl font-bold text-base hover:bg-neutral-100 transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isResetting ? 'Загрузка...' : 'Посмотреть заново'}
              </button>
              
              <button
                onClick={() => setActiveScreen('matches')}
                className="w-full h-14 bg-white/10 border border-white/20 text-white rounded-2xl font-semibold text-base hover:bg-white/20 transition-all active:scale-95"
              >
                Посмотреть совпадения
              </button>
            </div>

            {/* Tip */}
            <div className="mt-8 bg-white/5 border border-white/10 rounded-xl p-4">
              <p className="text-xs text-neutral-500">
                💡 <span className="text-neutral-400">Совет:</span> Загляните позже — новые профили появляются каждый день
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const currentProfile = profiles[currentIndex];
  const nextProfile = profiles[currentIndex + 1];

  const handleSwipe = (direction: SwipeDirection) => {
    if (!currentProfile) return;
    
    swipeProfile.mutate({
      targetId: currentProfile.id,
      direction,
    });
  };

  const handleCardClick = (userId: string) => {
    setViewingProfile(userId);
    TelegramUtils.haptic.selection();
  };

  const handleSwipeFromDetail = (userId: string, direction: 'like' | 'pass') => {
    swipeProfile.mutate({
      targetId: userId,
      direction,
    });
  };

  // Show ProfileDetailScreen if viewing a profile
  if (ui.viewingProfileId) {
    return (
      <ProfileDetailScreen
        userId={ui.viewingProfileId}
        onBack={() => setViewingProfile(null)}
        onSwipe={(direction) => {
          handleSwipeFromDetail(ui.viewingProfileId!, direction);
          setViewingProfile(null);
        }}
      />
    );
  }

  return (
    <div className="min-h-screen bg-tg-bg safe-area-y">
      {/* Header */}
      <div className="flex items-center justify-between py-3 border-b border-white/5 px-6">
        {/* Logo - Clickable to toggle feed type */}
        <button
          onClick={() => {
            toggleFeedType();
            TelegramUtils.haptic.selection();
          }}
          className="hover:opacity-80 transition-opacity"
        >
          <img 
            src={logo} 
            alt="ITinder" 
            className="h-7 w-auto"
          />
        </button>
        
        {/* Icons */}
        <div className="flex items-center gap-2">
          {/* Filter icon */}
          <button
            onClick={() => {
              setActiveScreen('filters');
              TelegramUtils.haptic.selection();
            }}
            className="relative h-7 w-7 flex items-center justify-center hover:opacity-80 transition-opacity"
          >
            <img 
              src={filterIconSrc} 
              alt="Filters" 
              className="h-6 w-6"
            />
            {hasActiveFilters && (
              <div className="absolute top-0 right-0 w-2 h-2 bg-primary-500 rounded-full border border-tg-bg" />
            )}
          </button>
          
          {/* Messages icon */}
          <button
            onClick={() => setActiveScreen('matches')}
            className="relative h-7 w-7 flex items-center justify-center hover:opacity-80 transition-opacity"
          >
            <img 
              src={matchIcon} 
              alt="Matches" 
              className="h-7 w-7"
            />
            {notificationCounters && notificationCounters.unread_messages_count > 0 && (
              <div className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-danger-500 rounded-full flex items-center justify-center border border-tg-bg">
                <span className="text-white text-xs font-bold leading-none">{notificationCounters.unread_messages_count}</span>
              </div>
            )}
          </button>
          
          {/* Profile icon */}
          <button
            onClick={() => setActiveScreen('profile')}
            className="h-7 w-7 flex items-center justify-center hover:opacity-80 transition-opacity"
          >
            <img 
              src={profileIcon} 
              alt="Profile" 
              className="h-6 w-6"
            />
          </button>
        </div>
      </div>

      {/* Card Stack */}
      <div className="flex-1 px-4 py-4 pb-32">
        <div className="relative w-full max-w-sm mx-auto" style={{ height: 'calc(100vh - 200px)' }}>
          {/* Background card (next profile) */}
          {nextProfile && (
            <div key={nextProfile.id} className="absolute inset-0 scale-95 opacity-30">
              <ProfileCard 
                profile={nextProfile} 
                currentPhotoIndex={0}
                onPhotoChange={() => {}} 
              />
            </div>
          )}
          
          {/* Current card */}
          {currentProfile && (
            <SwipeableCard 
              key={currentProfile.id}
              profile={currentProfile}
              onSwipe={handleSwipe}
              onCardClick={() => handleCardClick(currentProfile.id)}
            />
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="fixed bottom-0 left-0 right-0 safe-area-bottom z-50">
        <div className="flex items-center justify-center gap-3 p-4 bg-tg-bg/95 backdrop-blur-xl border-t border-white/5">
          <div className="flex items-center justify-center gap-3 w-full max-w-sm">
            <button
              onClick={() => handleSwipe('pass')}
              disabled={swipeProfile.isPending}
              className="flex-1 h-14 bg-white/10 border border-white/20 rounded-2xl text-white font-semibold hover:bg-white/20 transition-all active:scale-95 disabled:opacity-50"
            >
              Пропустить
            </button>
            
            <button
              onClick={() => handleSwipe('like')}
              disabled={swipeProfile.isPending}
              className="flex-1 h-14 bg-white text-black rounded-2xl font-bold hover:bg-neutral-100 transition-all active:scale-95 disabled:opacity-50"
            >
              ❤️ Нравится
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

interface SwipeableCardProps {
  profile: ProfileSummary;
  onSwipe: (direction: SwipeDirection) => void;
  onCardClick: () => void;
}

const SwipeableCard: React.FC<SwipeableCardProps> = ({ profile, onSwipe, onCardClick }) => {
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [{ x, y, rotate }, api] = useSpring(() => ({
    x: 0,
    y: 0,
    rotate: 0,
  }));

  const handleNextPhoto = () => {
    setCurrentPhotoIndex((prev) => 
      prev < profile.photos.length - 1 ? prev + 1 : 0
    );
    TelegramUtils.haptic.light();
  };

  const handlePrevPhoto = () => {
    setCurrentPhotoIndex((prev) => 
      prev > 0 ? prev - 1 : profile.photos.length - 1
    );
    TelegramUtils.haptic.light();
  };

  const bind = useDrag(
    ({ active, movement: [mx, my], direction: [xDir], velocity: [vx], tap, event }) => {
      // Handle tap with smart navigation
      if (tap) {
        // Get card width and click position
        const target = event?.target as HTMLElement;
        const card = target?.closest('.swipeable-card');
        if (card) {
          const rect = card.getBoundingClientRect();
          const clickX = (event as any).clientX || (event as any).changedTouches?.[0]?.clientX;
          if (clickX) {
            const relativeX = clickX - rect.left;
            const relativePosition = relativeX / rect.width;
            
            // If multiple photos, use smart zones
            if (profile.photos.length > 1) {
              if (relativePosition < 0.2) {
                // Left 20% - previous photo
                handlePrevPhoto();
                return;
              } else if (relativePosition > 0.8) {
                // Right 20% - next photo
                handleNextPhoto();
                return;
              }
            }
            
            // Center or single photo - open profile
            onCardClick();
          } else {
            // Fallback - just open profile
            onCardClick();
          }
        } else {
          onCardClick();
        }
        return;
      }
      
      const trigger = vx > 0.3 || Math.abs(mx) > 150;
      
      if (!active && trigger) {
        // Determine swipe direction
        const direction: SwipeDirection = mx > 0 ? 'like' : 'pass';
        
        // Haptic feedback
        TelegramUtils.haptic.light();
        
        // Animate off screen
        api.start({
          x: (200 + window.innerWidth) * xDir,
          y: my,
          rotate: mx / 100 + (xDir > 0 ? 1 : -1) * 15,
          config: { friction: 50, tension: 800 },
        });
        
        // Trigger swipe after animation
        setTimeout(() => onSwipe(direction), 250);
      } else {
        // Follow drag or return to center
        api.start({
          x: active ? mx : 0,
          y: active ? my : 0,
          rotate: active ? mx / 100 : 0,
          config: { friction: 50, tension: active ? 800 : 500 },
        });
      }
    },
    {
      filterTaps: true,
      pointer: { touch: true },
    }
  );

  return (
    <animated.div
      {...bind()}
      className="absolute inset-0 cursor-grab active:cursor-grabbing swipeable-card"
      style={{
        x,
        y,
        rotate: rotate.to(r => `${r}deg`),
        touchAction: 'none',
      }}
    >
      <ProfileCard 
        profile={profile} 
        currentPhotoIndex={currentPhotoIndex}
        onPhotoChange={setCurrentPhotoIndex}
      />
      
      {/* Swipe Overlays */}
      <animated.div
        className="absolute inset-0 bg-success-500/20 border-4 border-success-500 rounded-2xl flex items-center justify-center"
        style={{
          opacity: x.to([0, 100], [0, 1]),
        }}
      >
        <div className="text-6xl transform rotate-12 text-success-500 font-bold">
          LIKE
        </div>
      </animated.div>
      
      <animated.div
        className="absolute inset-0 bg-danger-500/20 border-4 border-danger-500 rounded-2xl flex items-center justify-center"
        style={{
          opacity: x.to([0, -100], [0, 1]),
        }}
      >
        <div className="text-6xl transform -rotate-12 text-danger-500 font-bold">
          PASS
        </div>
      </animated.div>
    </animated.div>
  );
};

interface ProfileCardProps {
  profile: ProfileSummary;
  currentPhotoIndex: number;
  onPhotoChange: (index: number) => void;
}

const ProfileCard: React.FC<ProfileCardProps> = ({ profile, currentPhotoIndex, onPhotoChange }) => {
  return (
    <div className="h-full rounded-3xl overflow-hidden bg-neutral-900 border border-white/10">
      {/* Photo Section */}
      <div className="relative h-full">
        <img
          src={profile.photos[currentPhotoIndex]}
          alt={profile.name}
          className="w-full h-full object-cover"
        />
        
        {/* Gradient overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
        
        {/* Photo Indicators */}
        {profile.photos.length > 1 && (
          <div className="absolute top-4 left-4 right-4 flex gap-1 z-10">
            {profile.photos.map((_, index) => (
              <div
                key={index}
                className={cn(
                  'flex-1 h-1 rounded-full transition-colors',
                  index === currentPhotoIndex ? 'bg-white' : 'bg-white/30'
                )}
              />
            ))}
          </div>
        )}
        
        {/* Info Section - Bottom */}
        <div className="absolute bottom-0 left-0 right-0 p-6 z-20">
          {/* Name and Age */}
          <div className="flex items-baseline gap-2 mb-2">
            <h3 className="text-3xl font-bold text-white">
              {profile.name}
            </h3>
            <span className="text-2xl text-white/80">
              {profile.age}
            </span>
          </div>
          
          {/* Level & Role & Score - for IT users */}
          {profile.role && (
            <div className="mb-3">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                {profile.level && (
                  <span className={cn(
                    'px-2 py-0.5 rounded-full text-sm font-semibold',
                    getBadgeLevelColor(profile.level)
                  )}>
                    {capitalizeBadgeLevel(profile.level)}
                  </span>
                )}
                <span className="text-white/70">
                  {profile.role}
                </span>
                {profile.verified && profile.itTestScore !== undefined && (
                  <span className={cn('font-bold', getScoreColor(profile.itTestScore))}>
                    • {profile.itTestScore}
                  </span>
                )}
              </div>
              {profile.company && (
                <p className="text-white/60 text-sm">
                  {profile.company}
                </p>
              )}
            </div>
          )}
          
          {/* Desired Lifestyle */}
          {profile.desiredLifestyle && (
            <div className="mb-3">
              <span className="text-sm text-white/80 bg-white/10 px-3 py-1.5 rounded-full">
                {profile.desiredLifestyle}
              </span>
            </div>
          )}
          
          {/* City/Country */}
          {(profile.city || profile.country) && (
            <div className="mb-3">
              <span className="text-sm text-white/60">
                📍 {[profile.city, profile.country].filter(Boolean).join(', ')}
              </span>
            </div>
          )}

          {/* Meeting Goals - первые 2 */}
          {profile.meetingGoals && profile.meetingGoals.length > 0 && (
            <div className="mb-3">
              <div className="flex flex-wrap gap-2">
                {profile.meetingGoals.slice(0, 2).map((goal) => (
                  <span key={goal.id} className="px-2 py-1 bg-white/20 backdrop-blur-sm rounded-full text-xs text-white font-medium">
                    {goal.icon} {goal.name}
                  </span>
                ))}
                {profile.meetingGoals.length > 2 && (
                  <span className="px-2 py-1 bg-white/20 backdrop-blur-sm rounded-full text-xs text-white font-medium">
                    +{profile.meetingGoals.length - 2}
                  </span>
                )}
              </div>
            </div>
          )}
          
          {/* Tech Stack - only if exists */}
          {profile.techStack && profile.techStack.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {profile.techStack.slice(0, 4).map((tech) => (
                <span key={tech} className="px-2 py-1 bg-white/20 backdrop-blur-sm rounded-full text-xs text-white font-medium">
                  {tech}
                </span>
              ))}
              {profile.techStack.length > 4 && (
                <span className="px-2 py-1 bg-white/20 backdrop-blur-sm rounded-full text-xs text-white font-medium">
                  +{profile.techStack.length - 4}
                </span>
              )}
            </div>
          )}
          
          {/* Common Hobbies - if exists */}
          {profile.commonHobbies && profile.commonHobbies.length > 0 && (
            <div className="mt-3">
              <div className="text-xs text-white/60 mb-2">
                💫 Общие интересы
              </div>
              <div className="flex flex-wrap gap-2">
                {profile.commonHobbies.slice(0, 3).map((hobby) => (
                  <span key={hobby} className="px-2 py-1 bg-primary-500/20 border border-primary-500/30 backdrop-blur-sm rounded-full text-xs text-primary-300 font-medium">
                    {hobby}
                  </span>
                ))}
                {profile.commonHobbies.length > 3 && (
                  <span className="px-2 py-1 bg-primary-500/20 border border-primary-500/30 backdrop-blur-sm rounded-full text-xs text-primary-300 font-medium">
                    +{profile.commonHobbies.length - 3}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FeedScreen;
